/* eslint-disable @next/next/no-img-element */
import { useEffect, useRef } from 'react';
import { Provider } from 'react-redux';
import { getCookie } from 'cookies-next';
import { AppInsightsContext, AppInsightsErrorBoundary } from '@microsoft/applicationinsights-react-js';
import appWithI18n from 'next-translate/appWithI18n';
import { config } from '@fortawesome/fontawesome-svg-core';
import { ToastContainer } from 'react-toastify';
import App, { type AppProps } from 'next/app';

import { enrichTelemetryData } from '@/telemetry/enrichTelemetryData';

import { DEFAULT_THEME, IS_CLIENT, IS_SERVER, GTM_QUEUE_STORAGE_KEY } from '@constants/constants';
import navigationService from '@/services/navigation-service';
import templateDataService, { ITemplateData } from '@/services/template-data-service';
import { cartApi } from '@/stores/cartApi';

import { Can, SecurityScope } from '@/utils/acl-utils';
import { useRouterState } from '@/hooks/useRouterState';
import { RootLayout } from '@/components/Layouts/RootLayout';
import { MclLayout } from '@/components/Layouts/MclLayout';
import { getRunningQueriesThunk } from '@/stores/baseApi';

import { currentUserApi, selectCurrentUser } from '@/stores/currentUserApi';
import { setPreference } from '@/stores/preferences';
import { useAppDispatch, useAppSelector, wrapper } from '@/configureStore';
import i18nConfig from '@/../i18n';
import { appInsights, reactPlugin, createTelemetryInitializer } from '@/telemetry/client';
import { redirectUser } from '@/utils/redirect';
import { FeatureTogglesProvider, fetchFeatureToggles, IFeatureToggles, isFeatureEnabled } from '@/services/feature-toggles-service';
import { usePageLoadEffect } from '@/hooks/usePageLoadEffect';
import { dispatchQueuedActions } from '@/stores/redux-action-queue-middleware';
import { registerCookiebotFlush, sendEnhancedConversion } from '@/utils/gtm-enhanced-conversion';

import type { NextPage } from 'next';

import 'react-toastify/dist/ReactToastify.css';
import '@fortawesome/fontawesome-svg-core/styles.css';
import '@styles/globals.scss';

export type PageWithLayout<P = unknown, IP = P> = NextPage<P, IP> & {
  getLayout?: (page: React.ReactElement, ...args: any[]) => React.ReactNode;
  securityScope?: SecurityScope;
  featureToggle?: string;
};

type MyAppProps = AppProps & {
  Component: AppContext['Component'];
  theme: AppTheme;
  pageProps: any;
  accessControlList?: SecurityScope;
  templateData: ITemplateData;
  featureToggles: IFeatureToggles;
  children?: React.ReactNode; // Added children property
};

// Prevent unstyled font-awsome icons on SSR
config.autoAddCss = false;

const EnhancedConversionListener = () => {
  const prev = useRef<string>();
  const dispatch = useAppDispatch();
  const email = useAppSelector((state) => selectCurrentUser(state)?.user?.email ?? '');

  useEffect(() => {
    if (email && email !== prev.current) {
      sendEnhancedConversion(email, dispatch);
    }
  }, [email, dispatch]);

  return null;
};

const MyApp = (props: MyAppProps) => {
  templateDataService.setTemplateData(props.templateData);
  const { store } = wrapper.useWrappedStore(props);
  const { Component, pageProps, theme, /* templateData,*/ featureToggles } = props;

  store.dispatch(
    setPreference({
      sector: props.theme
    })
  );

  useRouterState();

  usePageLoadEffect(() => {
    setTimeout(() => dispatchQueuedActions(store?.dispatch, GTM_QUEUE_STORAGE_KEY), 0);
  }, []);

  useEffect(() => {
    registerCookiebotFlush(store.dispatch);
  }, [store]);

  const getLayout = Component.getLayout ?? ((page: any) => <MclLayout>{page}</MclLayout>);

  const ErrorDisplay = () => <>{/* TODO: Make a decent Error component */}</>;

  return (
    <AppInsightsErrorBoundary onError={ErrorDisplay} appInsights={reactPlugin}>
      <AppInsightsContext.Provider value={reactPlugin}>
        <Provider store={store}>
          <EnhancedConversionListener />
          <FeatureTogglesProvider value={featureToggles}>
            <RootLayout theme={theme}>
              {getLayout(<Component {...pageProps}>{props?.children}</Component>)}
              <ToastContainer position="top-center" />
            </RootLayout>
          </FeatureTogglesProvider>
        </Provider>
      </AppInsightsContext.Provider>
    </AppInsightsErrorBoundary>
  );
};

(MyApp as any).getInitialProps = wrapper.getInitialAppProps((store: any) => async (context: AppContext) => {
  let theme = DEFAULT_THEME;

  try {
    const sectorCookie = getCookie('cl_sector', { req: context.ctx.req }) as string;
    theme = sectorCookie.toLowerCase();
  } catch {}

  navigationService.setSector(theme as AppSector);

  // Performance Optimization: Categories are now fetched lazily by individual layouts that need them.
  // This reduces initial page load size by ~4MB for pages that don't require navigation data.
  // Layouts using categories should call useCategories() hook to fetch on-demand.
  // See: ContentPageLayout.tsx, MclLayout.tsx for implementation examples.

  // await navigationService.fetchNavigationData(context.ctx.req);
  // const allCategories = navigationService.getAllCategories();

  const templateData = await templateDataService.fetchTemplateData(theme as AppSector);

  // Init Redux SSR
  store.dispatch(cartApi.endpoints.getCart.initiate());
  store.dispatch(currentUserApi.endpoints.getCurrentUser.initiate());

  await Promise.all(store.dispatch(getRunningQueriesThunk()));
  const initialProps = await App.getInitialProps(context);

  const featureToggles = await fetchFeatureToggles(context.ctx.req);

  if (featureToggles && context?.Component?.featureToggle) {
    if (!isFeatureEnabled(context.Component.featureToggle, featureToggles)) {
      redirectUser({
        request: context.ctx.req,
        response: context.ctx.res,
        statusMessage: `Feature not enabled: ${context.Component.featureToggle}`
      });
    }
  }

  const currentUser = selectCurrentUser(store);
  if (context?.Component?.securityScope && currentUser?.accessControlList) {
    if (!Can(context.Component.securityScope)(currentUser.accessControlList)) {
      redirectUser({
        request: context.ctx.req,
        response: context.ctx.res,
        statusMessage: `Permission denied: ${context.Component.securityScope}`
      });
    }
  }

  if (IS_SERVER) {
    enrichTelemetryData({
      customerNumber: currentUser?.customer?.customerNr,
      isLoggedIn: !!currentUser,
      userId: currentUser?.user?.userId,
      userName: currentUser?.user?.userName
    });

    if (IS_CLIENT) {
      const fn = createTelemetryInitializer({
        customerNumber: currentUser?.customer?.customerNr,
        isLoggedIn: !!currentUser,
        userId: currentUser?.user?.userId,
        userName: currentUser?.user?.userName
      });

      appInsights.addTelemetryInitializer(fn);

      if (!appInsights.appInsights.isInitialized()) {
        appInsights.loadAppInsights();
      }

      appInsights.trackPageView();
    }
  }

  return {
    ...initialProps,
    templateData,
    ...(currentUser ? { ...currentUser } : {}),
    theme,
    featureToggles
  };
});

// @ts-expect-error Type 'string' is not assignable to type '"both" | "node" | "browser" | undefined'.
export default appWithI18n(MyApp as any, {
  ...i18nConfig
});
